import { Box, Typography } from '@mui/material';
import { Dispatch, FC, SetStateAction, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectGetDeployedSchemesForEntityStatus } from '@pages/stands/store/selectors';
import CustomTable from '@shared/components/others/CustomTable/CustomTable';
import { Scheme, SchemeID } from '@pages/schemes/types';
import { MRT_ColumnDef } from 'material-react-table';
import { copyText, getSchemeLinkById } from '@shared/utils/utilsOld';
import MemoryIcon from '@mui/icons-material/Memory';
import SchemeConfiguration from './SchemeConfiguration';
import RamIcon from '@shared/static/RamIcon';
import { RequestStatusEnum } from '@shared/types';
import SchemePreviewDialog from '@pages/schemes/components/SchemePreviewDialog';

interface DeploySecondStepProps {
    isCurrentStep: boolean;
    schemesToDeploy: Scheme[];
    setSchemesToDeploy: Dispatch<SetStateAction<Scheme[]>>;
}

const DeploySecondStep: FC<DeploySecondStepProps> = ({ isCurrentStep, schemesToDeploy, setSchemesToDeploy }) => {
    const { t } = useTranslation();
    const getDeployedSchemesForEntityStatus = useSelector(selectGetDeployedSchemesForEntityStatus);

    const handleCopyLink = useCallback(async (schemeId: SchemeID) => {
        if (!schemeId) {
            return;
        }

        const schemeLink = getSchemeLinkById(schemeId, true);
        await copyText(schemeLink);
    }, []);

    const getSumByKeyFromScheme = useCallback(
        (scheme: Scheme, key: 'cpu' | 'memory') =>
            scheme.json_config.nodes.reduce((res, node) => {
                res += node.data[key] || 0;
                return res;
            }, 0),
        [],
    );

    const { totalMemory, totalCpu } = useMemo(() => {
        const { totalMemory, totalCpu } = schemesToDeploy.reduce(
            (res, scheme) => ({
                totalMemory: res.totalMemory + getSumByKeyFromScheme(scheme, 'memory'),
                totalCpu: res.totalCpu + getSumByKeyFromScheme(scheme, 'cpu'),
            }),
            { totalMemory: 0, totalCpu: 0 },
        );
        return { totalMemory, totalCpu };
    }, [getSumByKeyFromScheme, schemesToDeploy]);

    const columns = useMemo<MRT_ColumnDef<Scheme>[]>(
        () => [
            {
                accessorKey: 'preview',
                header: t('thumbnail'),
                size: 165,
                Cell: ({ cell }) =>
                    cell.getValue() ? (
                        <SchemePreviewDialog
                            scheme={cell.row.original}
                            sx={{ maxHeight: 192 }}
                            handleCopyLink={handleCopyLink}
                        />
                    ) : (
                        '—'
                    ),
                enableColumnFilter: false,
                enableSorting: false,
            },
            {
                accessorKey: 'memory',
                size: 55,
                minSize: 55,
                header: t('memory'),
                Cell: ({ cell }) => `${getSumByKeyFromScheme(cell.row.original, 'memory')} GB`,
                enableColumnFilter: false,
                enableSorting: false,
            },
            {
                accessorKey: 'cpu',
                size: 70,
                minSize: 70,
                header: t('cpu'),
                Cell: ({ cell }) => `${getSumByKeyFromScheme(cell.row.original, 'cpu')}`,
                enableColumnFilter: false,
                enableSorting: false,
            },
            {
                accessorKey: 'configuration',
                header: t('configuration'),
                Cell: ({ cell }) => (
                    <SchemeConfiguration scheme={cell.row.original} setSchemesToDeploy={setSchemesToDeploy} />
                ),
                enableColumnFilter: false,
                enableSorting: false,
            },
        ],
        [getSumByKeyFromScheme, handleCopyLink, setSchemesToDeploy, t],
    );

    return (
        <Box sx={{ display: isCurrentStep ? 'flex' : 'none', flexDirection: 'column', gap: 2 }}>
            {/*@ts-ignore*/}
            <CustomTable
                data={schemesToDeploy}
                columns={columns}
                enableColumnResizing={false}
                enablePagination={false}
                muiTableBodyProps={{
                    sx: {
                        height: 300,
                        maxWidth: '100%',
                    },
                }}
                renderBottomToolbar={() => <Box sx={{ display: 'none' }}></Box>}
                muiTablePaperProps={{
                    sx: {
                        width: '100%',
                        maxWidth: 850,
                        minWidth: 0,
                    },
                }}
                toolbar={() => <></>}
                variant="filled"
                state={{
                    showSkeletons: getDeployedSchemesForEntityStatus === RequestStatusEnum.Loading,
                }}
                defaultColumn={{
                    muiTableHeadCellProps: {
                        sx: {
                            pt: 2,
                            pb: 2,
                            pl: 2,
                            fontSize: 14,
                        },
                    },
                }}
                muiTableBodyRowProps={{
                    sx: {
                        cursor: '',
                    },
                }}
            />
            <Box>
                <Typography>{t('resources_required')}:</Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <MemoryIcon />
                        {`${totalMemory} ${t('gb')}`}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <RamIcon />
                        {`${totalCpu}`}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default DeploySecondStep;
